import apiRequest from './apiService';
import { SEARCH_MAX_LENGTH } from '../constants/dashboardConstants';
import { DashboardPaging } from '../interfaces/DashboardPaging';

const buildDashboardQuery = (
  companyId: string,
  userId: string | undefined,
  startDate: string,
  endDate: string,
  extra: Record<string, string> = {}
) => {
  const params = new URLSearchParams({
    company_id: companyId,
    start_date: startDate,
    end_date: endDate,
    ...extra,
  });
  if (userId) {
    params.set('user_id', userId);
  }
  return params.toString();
};

const getTopAgents = async (
  companyId: string,
  startDate: string,
  endDate: string,
  userId?: string
) => {
  try {
    const query = buildDashboardQuery(companyId, userId, startDate, endDate);
    const result = await apiRequest(
      'agent',
      'GET',
      `/dashboard/get_top_agents?${query}`
    );
    return result;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// The paging, sort, search and chip params both dashboards send
const pagingParams = (
  paging: DashboardPaging | undefined,
  categories: string[]
): Record<string, string> => ({
  ...(paging && {
    page: String(paging.page),
    page_size: String(paging.pageSize),
    ...(paging.sort && {
      sort_by: paging.sort.field,
      sort_dir: paging.sort.direction,
    }),
    ...(paging.search?.trim() && {
      search: paging.search.trim().slice(0, SEARCH_MAX_LENGTH),
    }),
  }),
  // one JSON list, so a comma inside a category name cannot split it
  ...(categories.length > 0 && { categories: JSON.stringify(categories) }),
});

const getDashboardData = async (
  companyId: string,
  startDate: string,
  endDate: string,
  agentIds?: string,
  userId?: string,
  paging?: DashboardPaging,
  // Selected chips. category_counts in the answer still covers the whole range.
  categories: string[] = []
) => {
  try {
    const query = buildDashboardQuery(companyId, userId, startDate, endDate, {
      agent_ids: agentIds ?? '',
      ...pagingParams(paging, categories),
    });
    const result = await apiRequest(
      'agent',
      'GET',
      `/dashboard/get_dashboard_data?${query}`
    );
    return result;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

const getTopChatAgents = async (
  companyId: string,
  startDate: string,
  endDate: string,
  userId?: string
) => {
  try {
    const query = buildDashboardQuery(companyId, userId, startDate, endDate);
    const result = await apiRequest(
      'chat_agent',
      'GET',
      `/dashboard/get_top_chat_agents?${query}`,
      null,
      null,
      10000
    );
    return result;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

const getChatDashboardData = async (
  companyId: string,
  startDate: string,
  endDate: string,
  agentIds?: string,
  userId?: string,
  paging?: DashboardPaging,
  categories: string[] = []
) => {
  try {
    const query = buildDashboardQuery(companyId, userId, startDate, endDate, {
      chat_agent_ids: agentIds ?? '',
      ...pagingParams(paging, categories),
    });
    const result = await apiRequest(
      'chat_agent',
      'GET',
      `/dashboard/get_chat_dashboard_data?${query}`,
      null,
      null,
      10000
    );
    return result;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export {
  getTopAgents,
  getDashboardData,
  getTopChatAgents,
  getChatDashboardData,
};
